import {
  ANNOUNCEMENT_RETENTION_DAYS,
  announcementReplyState,
  CONVERSATION_PREVIEW_MAX,
  type AnnouncementReplyState,
  type ConversationPreview,
} from "@edu/shared";
import { dayCountText, studentCountText } from "../../lib/plurals.ts";
import type { ApiErrorCode } from "../../lib/api.ts";

/**
 * Слова экранов переписки, отделённые от разметки.
 *
 * Зачем модуль. Здесь сходятся четыре правила, каждое из которых ломается молча
 * и видно только глазами на экране, поэтому они стоят чистыми функциями с
 * тестами, как формулировки панели «Где дыры»
 * (`features/students/student-skills-text.ts`):
 *
 * 1. **Состояний у сообщения три, а полей под них два.** Разбирать пару полей
 *    руками нельзя: разница между «сообщение удалили» и «прошёл срок хранения»
 *    видна только человеку и только словами. Пару разбирает готовая
 *    `announcementReplyState` из `@edu/shared`, здесь она превращается в слова
 * 2. **Сроки и потолки называются числами из контрактов**, а не написанными на
 *    глаз: зашитая тридцатка соврёт молча в тот день, когда срок поменяют, а
 *    зашитая сто двадцатка соврёт в день, когда сервер начнёт резать превью
 *    иначе
 * 3. ⚠️ **Про группу и про адресатов здесь не говорится ничего.** Кому что
 *    видно, решил сервер: заголовок окна приезжает готовым полем, подпись автора
 *    тоже. Своей подстановки имён в этом модуле нет ни одной
 * 4. ⚠️ **В адрес преподавателя не считается ничего** (раздел 5.10 CLAUDE.md).
 *    Считаются прочтения УЧЕНИКОВ, то есть то, что произошло с сообщением, а не
 *    то, как человек работает
 *
 * ⚠️ **`ApiErrorCode` импортируется ТИПОМ.** Значение утащило бы сюда весь
 * `lib/api.ts` вместе с `runtime-config`, а этот модуль обязан открываться в
 * голом Node: его читает `announcement-text.test.ts`. То же правило и по той же
 * причине стоит в `features/groups/groups-text.ts`.
 */

/**
 * Почему текста нет.
 *
 * ⚠️ Две причины и два разных предложения. `null` в теле означает «стёрто по
 * сроку», а не «пустое сообщение», и человек обязан различать это и удаление
 * руками: в первом случае текст не сохранился ни у кого, во втором его убрали.
 */
export function erasedText(): string {
  return `Текст больше не хранится: прошло ${dayCountText(ANNOUNCEMENT_RETENTION_DAYS)}`;
}

export const DELETED_TEXT = "Сообщение удалено";

/**
 * Текст сообщения и то, показывать ли его приглушённо.
 *
 * `dim` это не украшение: строка без текста остаётся в окне ради автора и
 * времени, но читать в ней нечего, и одинаковый вес с настоящими сообщениями
 * заставлял бы глаз спотыкаться о неё каждый раз.
 */
export function messageText(msg: { body: string | null; deletedAt: number | null }): {
  state: AnnouncementReplyState;
  text: string;
  dim: boolean;
} {
  const state = announcementReplyState(msg);
  if (state === "deleted") return { state, text: DELETED_TEXT, dim: true };
  if (state === "erased") return { state, text: erasedText(), dim: true };
  return { state, text: msg.body ?? "", dim: false };
}

/**
 * Тело сообщения строками, как его набрали.
 *
 * ⚠️ Переносы обязаны пережить показ, а `white-space: pre-wrap` в общих стилях
 * есть ровно у одного класса, и он про отказы. Поэтому строки рисуются
 * отдельными абзацами, и расстановка получается такой же, как в наборе, без
 * единого нового правила стилей.
 *
 * Пустые строки подряд схлопываются в одну: три пустых строки посреди
 * сообщения это промах по клавише, а не абзацная разрядка.
 */
export function bodyLines(body: string): string[] {
  const out: string[] = [];
  for (const raw of body.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trimEnd();
    if (line === "" && (out.length === 0 || out[out.length - 1] === "")) continue;
    out.push(line);
  }
  while (out.length > 0 && out[out.length - 1] === "") out.pop();
  return out;
}

/**
 * Строка списка разговоров: начало последнего сообщения.
 *
 * ⚠️ **Обрезал уже СЕРВЕР**, до `CONVERSATION_PREVIEW_MAX`, и обрезал по
 * знакам, то есть возможно посреди слова. Здесь остаются две вещи, которых
 * сервер не делал и делать не должен: переносы строк сплющиваются в пробелы
 * (иначе одна строка списка встала бы в три), и обрыв называется многоточием.
 *
 * ⚠️ **Признак «обрезали» это длина, сравнённая с константой ИЗ КОНТРАКТОВ.**
 * Своего числа здесь нет и быть не может: сервер режет по своему потолку, и
 * второе написание того же числа разошлось бы с первым в день, когда потолок
 * поменяют. Сообщение ровно в потолок длиной получит многоточие зря, и это
 * принятая цена: соврать «текст кончился» хуже, чем поставить лишнюю точку.
 *
 * ⚠️ **Меряется длина СЫРАЯ, до схлопывания переносов** (правка по итогам
 * итогового ревью 30.08.2026). Сервер режет по знакам того текста, который лежит
 * в базе, а схлопывание строк укорачивает его здесь: у объявления, набранного
 * абзацами, приехавшие сто двадцать знаков превращались в сотню, признак не
 * срабатывал, и обрубок последнего слова уезжал на экран без многоточия. То есть
 * ломалось это ровно там, где обрыв надо назвать громче всего.
 *
 * `null` в теле разбирается той же `messageText`, что и в окне: разбор пары
 * полей в двух местах однажды разъедется.
 */
export function previewText(last: Pick<ConversationPreview, "body" | "deletedAt">): string {
  const shown = messageText(last);
  if (shown.state !== "posted") return shown.text;
  const flat = shown.text.replace(/\s+/g, " ").trim();
  if (shown.text.length < CONVERSATION_PREVIEW_MAX) return flat;
  // Хвостовое слово обрывается сервером по знакам, поэтому убираем его целиком:
  // обрыв посреди слова читается как поломка шрифта. Половина считается от того,
  // что осталось после схлопывания, а не от потолка: у превью из абзацев это
  // разные числа, и от потолка порог отсекал бы слишком много
  const space = flat.lastIndexOf(" ");
  return `${(space > flat.length / 2 ? flat.slice(0, space) : flat).trimEnd()}…`;
}

/**
 * Что список разговоров приехал не целиком.
 *
 * ⚠️ **`truncated` считает сервер.** Своего счёта здесь нет и быть не может:
 * сравнивать длину списка с потолком значит повторять чужую арифметику. Числа в
 * подписи тоже нет: «показаны пятьдесят» под списком из пятидесяти строк это
 * пересказ экрана, а новость несёт вторая половина фразы.
 *
 * `null` означает «приехало всё», и тогда подписи нет вовсе: строка про
 * усечение под списком из двух переписок читается как «что-то от вас спрятали».
 *
 * ⚠️ **Слова «разговор» на экране нет** (правка по итогам итогового ревью
 * 30.08.2026). Это наше внутреннее слово: разговором в коде называется ключ
 * выборки, а человек видит список людей и групп, с которыми переписывается.
 */
export function conversationsTailText(truncated: boolean): string | null {
  if (!truncated) return null;
  return "Переписок больше, чем помещается в список. Показаны самые свежие.";
}

/**
 * Кто открыл сообщение. ⚠️ **Только у своих сообщений преподавателя.**
 *
 * ⚠️ Считаются прочтения УЧЕНИКОВ, а не работа преподавателя (раздел 5.10
 * CLAUDE.md): «прочитали 3 из 8» отвечает на вопрос «до кого дошло», и ни одна
 * подпись экрана не оценивает того, кто на неё смотрит.
 *
 * Двух слов на один случай не бывает, и развилка здесь ровно поэтому. В личном
 * окне получатель один, и «прочитали 1 из 1» это арифметика вместо ответа: там
 * вопрос «прочитал ли он», и отвечать на него надо словом. В групповом окне
 * получателей много, и тогда число это и есть ответ.
 *
 * `null` означает «подписи нет вовсе»: у сообщения ученика её быть не может, а у
 * рассылки, из которой все выбыли, показывать нечего.
 */
export function receiptText(recipients: number, seen: number): string | null {
  if (recipients <= 0) return null;
  if (recipients === 1) return seen > 0 ? "Прочитано" : "Не прочитано";
  /*
    Голыми числами, без слова «учеников»: «из 3 ученика» это то, что выдаёт
    общий помощник склонений (`studentCountText`), а правильное «из трёх
    учеников» требует родительного падежа, которого у него нет. Слово тут и не
    нужно: подпись стоит под сообщением, и о ком речь, видно из окна.
  */
  return `Прочитали ${String(seen)} из ${String(recipients)}`;
}

/**
 * Сколько человек в группе, подписью к списку имён в шапке окна.
 *
 * ⚠️ **Число берётся из длины приехавшего списка, а не из отдельного поля**, и
 * второго счёта тут нет: сервер отдаёт имена, и «в группе 8» под списком из семи
 * строк было бы враньём, которое никто не заметит. Форму числительного выбирает
 * общий помощник, поэтому «21 ученик», а не «21 учеников».
 *
 * `null` означает «подписи нет вовсе»: в группе, из которой убрали всех,
 * показывать нечего, а «в группе 0 учеников» это ответ на вопрос, которого никто
 * не задавал.
 *
 * ⚠️ Про роль читателя тут не говорится ничего: имена собрал сервер, и какие
 * именно приедут (из списка учеников преподавателя или внутригрупповые), решил
 * он же.
 */
export function groupMembersText(count: number): string | null {
  return count <= 0 ? null : `В группе ${studentCountText(count)}`;
}

/**
 * Почему отвечать больше нельзя.
 *
 * ⚠️ **Одна строка на два места, и это не экономия.** Раньше это был только
 * текст отказа `409 student_not_linked`, то есть узнавал о нём ученик
 * единственным способом: набрав ответ и нажав кнопку. Теперь окно приезжает с
 * `canReply`, и на месте формы стоит эта же фраза ЗАРАНЕЕ. Отказ при этом
 * остаётся: между чтением окна и нажатием пару можно отвязать. Разные слова в
 * двух местах означали бы, что одно и то же событие человек читает как два
 * разных.
 */
export const REPLIES_CLOSED_TEXT =
  "Отвечать больше нельзя: преподаватель отозвал вашу ссылку. Прошлые сообщения остаются.";

/**
 * Подпись значка «Убрать»: она же доступное имя кнопки.
 *
 * ⚠️ **Значку подпись нужна больше, чем словесной кнопке.** Кнопка со словом
 * «Удалить» читалке понятна сама по себе, а кнопка с одной картинкой для неё
 * пуста, и подписи ей взять неоткуда. Поэтому строка отсюда уезжает и в
 * `aria-label`, и в `title`: первое читает читалка, второе всплывает под
 * курсором.
 *
 * ⚠️ **Развилка по последствию, а не по роли.** Ответ уходит один, а сообщение
 * преподавателя уносит с собой и ответы под ним, и это разные события для того,
 * кто нажимает. Шаг подтверждения говорит об этом же полной фразой, но до
 * нажатия человек видит только значок, и последствие обязано быть слышно уже в
 * его имени.
 *
 * ⚠️ Ни «объявления», ни «нитки» в подписи нет: это наши слова, и наружу они не
 * едут (то же правило стоит у `conversation_not_found` ниже).
 */
export function removeMessageLabel(kind: "post" | "reply"): string {
  return kind === "post" ? "Удалить сообщение вместе с ответами" : "Удалить сообщение";
}

/**
 * Отказы переписки словами.
 *
 * ⚠️ **Словарь, а не разбор кода по месту.** Ключи здесь это коды из общего
 * словаря (`packages/shared/src/errors.ts`), поэтому переименование кода на
 * сервере роняет сборку клиента, а не уводит человека в ветку по умолчанию с
 * английским словом на экране. Тот же приём, что у групп
 * (`features/groups/groups-text.ts`) и у расписания (`schedule/common.ts`).
 *
 * ⚠️ **`student_not_linked` написан голосом УЧЕНИКА**, потому что читает его
 * почти всегда ученик: это отказ ответить. У формы отправки тот же код означает
 * другое («кому-то из выбранных отозвана ссылка»), и текст под неё она
 * подставляет сама.
 */
const ERROR_TEXTS: Readonly<Partial<Record<ApiErrorCode, string>>> = {
  /**
   * ⚠️ **«Переписка», а не «разговор»** (правка по итогам итогового ревью
   * 30.08.2026). Разговор это наше слово: так в коде зовётся ключ выборки, а
   * человек видит переписку с человеком или с группой. Наружу наш словарь не
   * едет, и это то же правило, по которому на экране нет ни «объявления», ни
   * «нитки».
   */
  conversation_not_found: "Такой переписки нет: её могли удалить, а ссылка могла быть чужой.",
  announcement_not_found: "Такого сообщения нет: его могли удалить, а ссылка могла быть чужой.",
  announcement_reply_not_found: "Этого сообщения больше нет. Обновите страницу.",
  announcement_empty: "Отправлять некому: в этом списке никого нет.",
  student_not_linked: REPLIES_CLOSED_TEXT,
  group_not_found: "Группа не найдена: обновите страницу.",
  invalid_cursor: "Не удалось показать более ранние сообщения. Обновите страницу.",
};

/**
 * Отказ словами. `fallback` это `message` у `ApiError`: отказы чужих разделов и
 * всё, чего нет в словаре, обязаны доезжать как есть, а не превращаться в
 * «что-то пошло не так».
 */
export function announcementErrorText(code: ApiErrorCode, fallback: string): string {
  return ERROR_TEXTS[code] ?? fallback;
}
